import { toast } from "react-toastify";
import { useGenericModal } from "src/components/reusable/components/GenericModal";
import { useAnotherGroupWarningModal } from "src/components/reusable/components/GlobalModals/AnotherGroupWarningModal";
import { useBallparkJoinGroupNoPassErrorModal } from "src/components/reusable/components/GlobalModals/BallparkJoinGroupNoPassErrorModal";
import { useCreateGroupConfirmModal } from "src/components/reusable/components/GlobalModals/CreateGroupConfirmModal";
import { useLeaveGroupConfirmModal } from "src/components/reusable/components/GlobalModals/LeaveGroupConfirmModal";
import { usePageTopNotification } from "src/components/reusable/components/PageTopNotification";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSeatGeek } from "src/components/reusable/hooks/useSeatGeek";
import { useSSAuth } from "src/components/reusable/hooks/useSSAuth";
import { useTicketmaster } from "src/components/reusable/hooks/useTicketmaster";
import { useTicketsdotcom } from "src/components/reusable/hooks/useTicketsdotcom";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { constants } from "src/shared/constants";
import { globalLanguages } from "src/shared/global_languages";
import { routes } from "src/shared/routes";
import { TGroupDetails, TPass, TPassOnAccount } from "src/shared/types/globalStotre.types";

// sample invite link :
// http://localhost:3500/pass_test/group_invite?groupLink=uE_bpEa7WBtjr_wR_ncg&inviteeUserId=38

export function useGroupInviteFlow() {
  const { TEAM_GLOBAL_SETTINGS, VENDOR } = useTeamConfig();
  const router = useRouterExt();
  const { connectTicketmaster } = useTicketmaster();
  const { connectSeatGeek } = useSeatGeek();
  const { connectTicketsdotcom } = useTicketsdotcom();
  const { setGlobalState, groupDetails, session, invitationGroupDetails } =
    useGlobalStore();
  const groupLink = String(router?.query?.groupLink);
  const inviteeUserId = Number(router?.query?.inviteeUserId);
  const isGroupInviteFlow = !!groupLink && !!inviteeUserId;
  const leaveGroupConfirmModal = useLeaveGroupConfirmModal();
  const groupLeader = invitationGroupDetails?.groupMembers?.find((itm) => {
    return itm.isLeader;
  });
  const pageTopNotification = usePageTopNotification();
  const anotherGroupWarningModal = useAnotherGroupWarningModal();
  const ballparkJoinGroupNoPassErrorModal =
    useBallparkJoinGroupNoPassErrorModal();
  const genericModal = useGenericModal();
  const {
    getUserGroupApi,
    createGroupApi,
    removeGroupMemberApi,
    leaveGroupApi,
    joinGroupApi,
    getGroupDetailsApi,
    getPassesOnAccountApi,
    validateGroupRestrictionsApi,
  } = useApiRoutes();
  const { user } = useSSAuth();

  const handleGetUserGroup = async (): Promise<TGroupDetails> => {
    const groupDetails = await getUserGroupApi.get();
    setGlobalState({ groupDetails });
    return groupDetails;
  };

  const handleGetInvitationGroupDetails = async () => {
    const invitationGroupDetails = await getGroupDetailsApi.post({ groupLink });
    setGlobalState({ invitationGroupDetails });
    return invitationGroupDetails;
  };

  const handleCreateGroup = async () => {
    try {
      await createGroupApi.get();
      await handleGetUserGroup();
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const handleRemoveGroupMember = async (memberUserId: number) => {
    try {
      await removeGroupMemberApi.post({ memberUserId });
      await handleGetUserGroup();
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await leaveGroupApi.post({ groupId: groupDetails?.groupId });
      await handleGetUserGroup();
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const setGroupInvitePurchaseSuccessMsg = () => {
    const TITLE = globalLanguages.GROUP_INVITE_PURCHASE_SUCCESS_MESSAGE.TITLE;
    const DESCRIPTION =
      globalLanguages.GROUP_INVITE_PURCHASE_SUCCESS_MESSAGE.DESCRIPTION;
    pageTopNotification.set({
      isOpen: true,
      route: routes.home,
      title: <TITLE />,
      description: <DESCRIPTION />,
    });
  };

  const setSwafPromptGetStartSuccessMsg = () => {
    const TITLE = globalLanguages.GROUP_INVITE_PURCHASE_SUCCESS_MESSAGE.TITLE;
    const DESCRIPTION =
      globalLanguages.GROUP_INVITE_PURCHASE_SUCCESS_MESSAGE.DESCRIPTION;
    pageTopNotification.set({
      isOpen: true,
      route: routes.my_group,
      title: <TITLE />,
      description: <DESCRIPTION />,
    });
  };

  const fireJoinGroupErrorModal = (error: any) => {
    genericModal.set({
      isOpen: true,
      title: "Error",
      hideTitleIcon: true,
      isConfirmButtonHidden: true,
      description: findApiErrorMessage(error),
    });
  };

  const fireGroupNotFoundModal = (message?: string) => {
    genericModal.set({
      isOpen: true,
      title: "Unable to Join Group",
      description:
        message ||
        "This group no longer exists. Please log in to create a group and invite your friends to join.",
      isConfirmButtonHidden: true,
      hideTitleIcon: true,
      onClickClose: async () => {
        await router.pushSchema(
          TEAM_GLOBAL_SETTINGS?.DEFAULT_APP_ROUTE || routes.landing
        );
      },
    });
  };

  const fireInvalidGroupModal = (message?: string) => {
    genericModal.set({
      isOpen: true,
      title: "Unable to Join Group",
      description:
        message ||
        "The group you’re trying to access is invalid, please verify the link. If you believe this is an error, contact support for assistance.",
      isConfirmButtonHidden: true,
      hideTitleIcon: true,
      onClickClose: async () => {
        await router.pushSchema(
          TEAM_GLOBAL_SETTINGS?.DEFAULT_APP_ROUTE || routes.landing
        );
      },
    });
  };

  const handleJoinGroup = async () => {
    if (!isGroupInviteFlow) return;
    try {
      await joinGroupApi.post({ groupLink, inviteeUserId });
    } catch (err) {
      fireJoinGroupErrorModal(err);
    }
  };

  const isUserAlreadyMember = (invtGroupDetails: TGroupDetails) => {
    const { groupMembers } = invtGroupDetails;
    return groupMembers.some((member) => {
      return member.userId == user?.userId;
    });
  };

  function isUserInAnotherGroup<T extends TGroupDetails>(
    usrGroupDetails: T,
    invtGroupDetails: T
  ) {
    return Boolean(
      usrGroupDetails?.groupId &&
        usrGroupDetails?.groupId !== invtGroupDetails?.groupId
    );
  }

  const handleLeaveAndJoinGroup = async () => {
    try {
      await handleLeaveGroup();
      await handleJoinGroup();
    } catch (err) {
      fireJoinGroupErrorModal(err);
    }
  };

  const onLoadGroupInvitePage = async () => {
    try {
      const inviteeUserId = Number(router?.query?.inviteeUserId);
      let usrGroupDetails: TGroupDetails | null = null;
      let invtGroupDetails: TGroupDetails | null = null;
      let passesOnAccount: TPassOnAccount[] = [];
      const isCheckout = router?.query?.tab === constants.checkoutTabs.checkout;

      if (user) {
        usrGroupDetails = await handleGetUserGroup();
        passesOnAccount = await getPassesOnAccountApi.get();
      }
      if (groupLink) {
        invtGroupDetails = await handleGetInvitationGroupDetails();
        if (!invtGroupDetails?.groupId && invtGroupDetails?.message) {
          return fireGroupNotFoundModal(invtGroupDetails?.message);
        }
      }

      if (!invtGroupDetails || !usrGroupDetails || !user) return;

      const invtGroupValidation = await validateGroupRestrictionsApi.post({
        inviteeUserId,
      });

      if (!invtGroupValidation?.isValidGroup) {
        const errorMessage = invtGroupValidation?.error?.message;
        return fireInvalidGroupModal(errorMessage);
      }

      if (isUserInAnotherGroup(usrGroupDetails, invtGroupDetails)) {
        return anotherGroupWarningModal.set({ isOpen: true });
      }

      if (isUserAlreadyMember(invtGroupDetails)) {
        return await router.pushSchema(routes.my_group);
      }

      if (isCheckout) {
        return router.push({ pathname: routes.checkout, query: router.query });
      }

      if (user && TEAM_GLOBAL_SETTINGS?.IS_BALLPARK_APP) {
        if (passesOnAccount.length > 0) {
          await handleJoinGroup();
          return await router.pushSchema(routes.my_group);
        } else {
          return ballparkJoinGroupNoPassErrorModal.set({ isOpen: true });
        }
      }

      if (
        user &&
        (TEAM_GLOBAL_SETTINGS?.DISABLE_PASS_ON_ACCOUNT_RESTRICTION ||
          passesOnAccount.length > 0)
      ) {
        await handleJoinGroup();
        return await router.pushSchema(routes.my_group);
      }
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const onClickGroupInviteSubscribe = (passId: number) => {
    const groupLink = router?.query?.groupLink;
    const inviteeUserId = router?.query?.inviteeUserId;

    if (session) {
      router.pushSchema({
        pathname: routes.checkout,
        query: {
          tab: "checkout",
          passId: String(passId),
          groupLink: groupLink || undefined,
          inviteeUserId: inviteeUserId || undefined,
        },
      });
      return;
    } else {
      const state = {
        authOrigin: constants.authOrigins.groupInvitePurchase,
        redirectRoute: {
          pathname: routes.group_invite,
          query: {
            tab: constants.checkoutTabs.checkout,
            passId: String(passId),
            groupLink: groupLink || undefined,
            inviteeUserId: inviteeUserId || undefined,
          },
        },
      };

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return connectTicketsdotcom(state);
      }

      connectTicketmaster({ state });
    }
  };

  const onClickGroupInviteLogin = async () => {
    const state = {
      authOrigin: constants.authOrigins.groupInviteLogin,
      redirectRoute: {
        pathname: routes.group_invite,
        query: {
          groupLink: groupLink || undefined,
          inviteeUserId: String(inviteeUserId) || undefined,
        },
      },
    };

    if (VENDOR === constants.vendorUUIDs.seatgeek) {
      return await connectSeatGeek(state);
    }

    if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
      return await connectTicketsdotcom(state);
    }

    await connectTicketmaster({ state });
  };

  const onClickLeaveGroup = async () => {
    leaveGroupConfirmModal.set({
      isOpen: true,
      groupDetails,
      onConfirm: async () => {
        await handleLeaveGroup();
      },
    });
  };

  const onClickPassGroupPurchase = async () => {
    const groupLink = router?.query?.groupLink;
    const inviteeUserId = router?.query?.inviteeUserId;

    if (session) {
      router.pushSchema({
        pathname: routes.checkout,
        query: {
          tab: "checkout",
          groupLink: groupLink || undefined,
          inviteeUserId: inviteeUserId || undefined,
        },
      });
      return;
    } else {
      const state = {
        authOrigin: constants.authOrigins.groupInvitePurchase,
        redirectRoute: {
          pathname: routes.group_invite,
          query: {
            tab: constants.checkoutTabs.checkout,
            groupLink: groupLink || undefined,
            inviteeUserId: inviteeUserId || undefined,
          },
        },
      };

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return connectTicketsdotcom(state);
      }

      connectTicketmaster({ state });
    }
  };

  return {
    handleGetUserGroup,
    handleCreateGroup,
    handleRemoveGroupMember,
    handleLeaveGroup,
    onClickGroupInviteSubscribe,
    handleJoinGroup,
    handleGetInvitationGroupDetails,
    onLoadGroupInvitePage,
    handleLeaveAndJoinGroup,
    isGroupInviteFlow,
    groupLink,
    inviteeUserId,
    groupLeader,
    onClickGroupInviteLogin,
    setGroupInvitePurchaseSuccessMsg,
    setSwafPromptGetStartSuccessMsg,
    onClickLeaveGroup,
    onClickPassGroupPurchase,
  };
}
